import { decode } from 'html-entities'

import { Regions } from '@awaze/mfe-common.types-and-mocks.one-search-client'
import type {
  CommonSite,
  ICommonAggregations,
  SearchCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { formatPropertyPageURL } from '@awaze/mfe-common.utils.pdp-url'
import { isPopularDestination } from '@awazevr/design-system.utils.form'
import {
  MapProductClickPayload,
  MapProductImpressionPayload,
} from '@awazevr/mfe-common.contexts.tms'

import {
  DEFAULT_IMAGE_URL,
  IMAGE_DOMAIN_BASE_URL,
  IMAGE_WIDTH_AWAZE,
  IMAGE_WIDTH_NOVASOL,
} from '@/constants'
import { removeFloorplanImagesFromImageArray } from '@/containers/SearchResults/helpers'

interface Coordinate {
  lat: number
  lon: number
}

interface Bounds {
  ne: Coordinate
  sw: Coordinate
}

export const getCoordinatesFromBounds = (
  bounds: google.maps.LatLngBounds,
): Bounds => ({
  ne: {
    lat: bounds.getNorthEast().lat(),
    lon: bounds.getNorthEast().lng(),
  },
  sw: {
    lat: bounds.getSouthWest().lat(),
    lon: bounds.getSouthWest().lng(),
  },
})

type MapImpressionAndClickPayload =
  | MapProductImpressionPayload
  | MapProductClickPayload

export const buildMapImpressionAndClickPayload = (
  searchCriteria: SearchCriteria,
  aggregations: ICommonAggregations,
  rhsLevel: Regions,
  total: number,
  propertyInfo: CommonSite,
): MapImpressionAndClickPayload => {
  return {
    searchCriteria,
    searchData: {
      aggregations,
      properties: [propertyInfo],
      rhsLevel,
      total,
    },
    propertyCode: propertyInfo.code,
  }
}

type PropertyInfoSubsetForUrlGeneration = Pick<
  CommonSite,
  'displayName' | 'code' | 'startDate' | 'lengthOfStay'
>

export const buildPropertyUrlForMapProperties = (
  product: string,
  property: PropertyInfoSubsetForUrlGeneration,
  searchCriteria: Partial<SearchCriteria>,
  additionalSearchParams: Record<string, string> = {},
): string => {
  const { startDate = '', displayName, code } = property
  const [startYear, startMonth, startDay] = startDate.split('-')

  const searchParamsWithProductStartDate = {
    ...searchCriteria,
    ...(property.startDate && {
      start: `${startDay}-${startMonth}-${startYear}`,
    }),
    ...(property.lengthOfStay && {
      nights: property.lengthOfStay.toString(),
    }),
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const params = new URLSearchParams([
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ...Object.entries(searchParamsWithProductStartDate),
    ...Object.entries(additionalSearchParams),
  ])
  const searchParameters = params.toString()

  return formatPropertyPageURL(
    product,
    // Types need to be changed upstream - formatPropertyPageURL can actually handle undefined fine
    // So this should be `string | undefined`
    displayName as string,
    code,
    searchParameters,
  )
}

export const removePopularDestinationFeatureIds = (
  featureIds: string[],
): string[] => {
  return featureIds.filter((featureId) => {
    return !isPopularDestination(featureId)
  })
}

export const doesStringContainASlash = (
  arg: string,
): arg is `${string}/${string}` => {
  return /^[^/]+\/[^/]+$/.test(arg)
}

export const formatNovasolImageURL = (
  url: `${string}/${string}`,
  width: number,
): string => {
  const JPGName = url.split('/')[1]
  const firstThreeLetters = JPGName.slice(0, 3)
  return `//image.novasol.com/pic/${width}/${firstThreeLetters}/${JPGName}`
}

export const formatUKBrandsImageURL = (
  domain: string,
  imageId: string,
  width: number,
): string => {
  return `${domain}/${imageId.substring(0, 3)}/${width}/${imageId}.jpg`
}

export const modifyPropertyImages = (
  images: Exclude<CommonSite['images'], undefined>,
): CommonSite['images'] => {
  if (images.length === 0) {
    return images
  }

  let modifiedImages: CommonSite['images'] = []
  const { assetId } = images[0]

  let imageURL: string = DEFAULT_IMAGE_URL
  if (assetId && doesStringContainASlash(assetId)) {
    imageURL = formatNovasolImageURL(assetId, IMAGE_WIDTH_NOVASOL)
  } else if (assetId) {
    imageURL = formatUKBrandsImageURL(
      IMAGE_DOMAIN_BASE_URL,
      assetId,
      IMAGE_WIDTH_AWAZE,
    )
  }

  if (images !== undefined) {
    modifiedImages = [
      {
        ...images[0],
        assetId: imageURL,
      },
      ...images.slice(1),
    ]
  }

  return modifiedImages
}

export const handlePropertyImages = (
  propertyInfo: CommonSite | null,
): {
  url: string
  altText: string
} => {
  if (!propertyInfo || !propertyInfo.displayName) {
    return {
      url: DEFAULT_IMAGE_URL,
      altText: 'Property images coming soon',
    }
  }
  const defaultImage = {
    url: DEFAULT_IMAGE_URL,
    altText: `${decode(propertyInfo.displayName)} images coming soon`,
  }

  const imageArray = propertyInfo.images

  if (!imageArray || imageArray.length === 0) {
    return defaultImage
  }

  const imageArrayWithoutFloorplans =
    removeFloorplanImagesFromImageArray(imageArray)

  if (imageArrayWithoutFloorplans.length === 0) {
    return defaultImage
  }
  return {
    url: imageArrayWithoutFloorplans[0].assetId,
    altText: imageArrayWithoutFloorplans[0].caption || '',
  }
}
